import { useCallback } from 'react';

interface HeroSectionProps {
  navigateTo: (path: string, key: string) => void;
  isLoading: (key: string) => boolean;
}

const STATS = [
  {
    icon: '💑',
    value: 'Feito para você',
    label: 'Experiência personalizada com base no perfil do casal',
    color: 'from-rose-50 to-pink-50',
    border: 'border-rose-100',
  },
  {
    icon: '🎯',
    value: '4.200+',
    label: 'Missões disponíveis',
    color: 'from-purple-50 to-pink-50',
    border: 'border-purple-100',
  },
  {
    icon: '✨',
    value: '98%',
    label: 'Satisfação dos casais',
    color: 'from-amber-50 to-rose-50',
    border: 'border-amber-100',
  },
  {
    icon: '💬',
    value: '24h',
    label: 'Layla disponível',
    color: 'from-pink-50 to-purple-50',
    border: 'border-pink-100',
  },
];

export default function HeroSection({ navigateTo, isLoading }: HeroSectionProps) {
  const scrollToHowItWorks = useCallback(() => {
    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section className="relative pt-36 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-100/40 via-rose-100/40 to-amber-100/40" />
      <div className="absolute top-20 left-10 w-80 h-80 bg-gradient-to-br from-rose-300/30 to-pink-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-br from-purple-300/25 to-amber-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-64 bg-gradient-to-r from-pink-200/15 via-rose-200/15 to-purple-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200">
            <span className="text-xl">💕</span>
            <span className="text-sm font-medium text-gray-700">Plataforma inteligente para casais</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
            Reconecte-se com
            <span className="block bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
              quem você ama
            </span>
          </h1>
        </div>

        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed text-center">
          Missões personalizadas, experiências únicas e uma consultora especializada em
          relacionamentos — tudo para fortalecer o vínculo de vocês dois.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button
            onClick={() => navigateTo('/cadastro', 'hero-cadastro')}
            disabled={isLoading('hero-cadastro')}
            className="w-full sm:w-auto px-8 py-4 text-lg font-medium text-white bg-gradient-to-r from-rose-500 to-pink-500 rounded-full hover:from-rose-600 hover:to-pink-600 transition-all shadow-lg whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading('hero-cadastro') ? (
              <><i className="ri-loader-4-line animate-spin" /> Aguarde...</>
            ) : (
              'Começar Gratuitamente'
            )}
          </button>
          <button
            onClick={scrollToHowItWorks}
            className="w-full sm:w-auto px-8 py-4 text-lg font-medium text-gray-700 bg-white rounded-full hover:bg-gray-50 transition-all border border-gray-200 whitespace-nowrap cursor-pointer"
          >
            Como Funciona
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-16 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-green-500 text-lg" />
            <span>Grátis para começar</span>
          </div>
          <span className="text-gray-300">•</span>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-green-500 text-lg" />
            <span>IA especializada em relacionamentos</span>
          </div>
          <span className="text-gray-300">•</span>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-green-500 text-lg" />
            <span>Personalizado para todo casal</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className={`bg-gradient-to-br ${stat.color} border ${stat.border} rounded-2xl px-5 py-5 text-center`}
            >
              <div className="text-3xl mb-2">{stat.icon}</div>
              <div className="text-2xl font-bold text-gray-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
